import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getFeatureModules } from '../api/entitlementsApi';
import { colorTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { IconButton } from '../components/ui/IconButton';

const { semantic } = colorTokens;

const ACCESS_CATEGORY_TONE = {
  core: 'Brand',
  paid_module: 'Success',
  future_override: 'Warning',
  support_dual_flow: 'Info',
};

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

function billingStatusTone(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'active' || normalized === 'enabled') return 'Success';
  if (normalized === 'inactive' || normalized === 'disabled' || normalized === 'deprecated') return 'Neutral';
  if (normalized === 'draft' || normalized === 'pending') return 'Warning';
  if (normalized === 'error' || normalized === 'failed' || normalized === 'blocked') return 'Danger';
  return 'Neutral';
}

function YesNoBadge({ value, t }) {
  return (
    <StatusBadge
      status={value ? 'yes' : 'no'}
      category="generic"
      apiLabel={value ? t('common:yes') : t('common:no')}
      size="Sm"
      tone={value ? 'Success' : 'Neutral'}
    />
  );
}

export function FeatureModulesPage() {
  const { t } = useTranslation(['features', 'navigation', 'common', 'setupProfiles']);
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const emptyValue = t('common:empty_value');
  const recordCount = meta?.total != null ? meta.total : modules.length;

  const loadModules = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getFeatureModules({ source: 'mafertapp' });
      setModules(response.data || []);
      setMeta(response.meta || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const supportGapRows = useMemo(
    () => modules.filter((row) => row.support_permission_gap),
    [modules],
  );

  const columns = useMemo(() => [
    {
      id: 'customer_name',
      label: t('common:module'),
      render: (row) => row.customer_name || row.module_code || emptyValue,
    },
    {
      id: 'billing_code',
      label: t('features:columns.billingCode'),
      render: (row) => (
        row.billing_code
          ? <Typography sx={monoSx}>{row.billing_code}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'mafertapp_parent',
      label: t('features:columns.mafertappParent'),
      render: (row) => (
        row.mafertapp_parent
          ? <Typography sx={monoSx}>{row.mafertapp_parent}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'access_category',
      label: t('common:category'),
      render: (row) => (
        row.access_category
          ? (
            <StatusBadge
              status={row.access_category}
              category="generic"
              apiLabel={row.access_category}
              size="Sm"
              tone={ACCESS_CATEGORY_TONE[row.access_category] || 'Neutral'}
            />
          )
          : emptyValue
      ),
    },
    {
      id: 'billing_status',
      label: t('features:columns.billingStatus'),
      render: (row) => (
        <StatusBadge
          status={row.billing_status || 'unknown'}
          category="plan"
          size="Sm"
          tone={billingStatusTone(row.billing_status)}
        />
      ),
    },
    {
      id: 'public_pricing',
      label: t('features:columns.publicPricing'),
      render: (row) => <YesNoBadge value={Boolean(row.public_pricing)} t={t} />,
    },
    {
      id: 'permission_rows_count',
      label: t('features:permissionRows'),
      render: (row) => (
        <Typography sx={monoSx}>{row.permission_rows_count ?? 0}</Typography>
      ),
    },
    {
      id: 'permission_strings_count',
      label: t('features:permissionStrings'),
      render: (row) => (
        <Typography sx={monoSx}>{row.permission_strings_count ?? 0}</Typography>
      ),
    },
    {
      id: 'functional_capabilities_count',
      label: t('features:functionalCapabilities'),
      render: (row) => (
        <Typography sx={monoSx}>{row.functional_capabilities_count ?? 0}</Typography>
      ),
    },
    {
      id: 'global_dependencies_count',
      label: t('features:globalDependencies'),
      render: (row) => (
        <Typography sx={monoSx}>{row.global_dependencies_count ?? 0}</Typography>
      ),
    },
    {
      id: 'seed_scope',
      label: t('features:columns.seedScope'),
      render: (row) => (
        row.seed_scope
          ? <Typography sx={monoSx}>{row.seed_scope}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'setup_profile',
      label: t('setupProfiles:profile'),
      render: () => t('setupProfiles:viewProfile'),
    },
    {
      id: 'source',
      label: t('features:columns.source'),
      render: (row) => (
        row.source
          ? (
            <StatusBadge
              status={row.source}
              category="generic"
              apiLabel={row.source}
              size="Sm"
              tone="Neutral"
            />
          )
          : emptyValue
      ),
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      width: 72,
      render: (row) => (
        <IconButton
          size="Sm"
          tone="Neutral"
          aria-label={t('setupProfiles:viewProfile')}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/setup/module-profiles?module_code=${row.module_code}`);
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      ),
    },
  ], [emptyValue, navigate, t]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('navigation:technicalFeatureModules')}
        subtitle={t('features:mafertAppMappingSource')}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button variant="Secondary" component={RouterLink} to="/billing/module-prices">
              {t('features:openModulePricing')}
            </Button>
            <Button variant="Secondary" component={RouterLink} to="/system-modules/mafertapp">
              {t('features:openMafertAppModules')}
            </Button>
          </Stack>
        )}
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadModules} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('features:modules.load_failed')}
        </Alert>
      )}

      {!error && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('features:notModulePricing')}
          </Alert>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('features:sourceOfTruth')}
          </Alert>
          {supportGapRows.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('features:supportPermissionGap')}
            </Alert>
          )}
          {meta?.total != null && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('features:modules.summary', { count: meta.total, source: meta.source })}
            </Alert>
          )}
        </>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('navigation:technicalFeatureModules')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('features:modules.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={modules}
            getRowKey={(row) => row.module_code}
            emptyMessage={t('features:modules.no_modules')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>
    </Box>
  );
}
